/**
 * @fileoverview Link component that integrates with the router context for
 * SPA navigation. Falls back to standard anchor behavior when no router is present.
 */

import { type AnchorHTMLAttributes, type ForwardedRef, type MouseEvent, forwardRef, useContext, useMemo } from 'react';
import { RouterContext } from '../contexts';

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /**
   * History navigation mode:
   * - "replace" replaces the current history entry
   * - "push" adds a new history entry
   * - "auto" (default) mimics "push" behavior
   */
  history?: 'auto' | 'push' | 'replace';
  href: string;
}

export const DefaultLink = forwardRef(function DefaultLink(
  props: LinkProps,
  ref: ForwardedRef<HTMLAnchorElement>,
) {
  const { children, href, onClick, history, ...rest } = props;
  const routerContext = useContext(RouterContext);

  const handleLinkClick = useMemo(() => {
    if (!routerContext) {
      return onClick;
    }
    return (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      const url = new URL(event.currentTarget.href);
      routerContext.navigate(url.pathname, { history });
      onClick?.(event);
    };
  }, [routerContext, onClick, history]);

  return (
    <a ref={ref} href={href} {...rest} onClick={handleLinkClick}>
      {children}
    </a>
  );
});

export const Link = forwardRef(function Link(
  props: LinkProps,
  ref: ForwardedRef<HTMLAnchorElement>,
) {
  const routerContext = useContext(RouterContext);
  const LinkComponent = routerContext?.Link ?? DefaultLink;
  return <LinkComponent ref={ref} {...props} />;
});
